import { Entity } from '@api';
import { usePriceMarketReactionData } from '@terminal/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { LineData } from '@terminal/modules/market-reaction-charts/types';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { useThemePerformanceTimeseries } from '@terminal/react-query-hooks/themes/useThemePerformanceTimeseries';
import { useFlags } from '@terminal/stores/use-flags/useFlags';

import { MarketCatalystReactionChartData } from '../market-catalyst-reaction.types';
import { buildSingleEtfLine } from '../utils/buildSingleEtfLine';

type UseSingleEtfMarketReactionLinesArgs = {
  tags: string[];
  tickers: string[];
  createdAt: string;
  themeId?: string;
};

export const useSingleEtfMarketReactionLines = ({
  tags,
  tickers,
  createdAt,
  themeId,
}: UseSingleEtfMarketReactionLinesArgs): MarketCatalystReactionChartData => {
  const isIntradayChartEnabled = useFlags(state =>
    state.getFeatureFlagState('theme_intraday_chart')
  );
  const isThemeTimeseriesEnabled = !!themeId && isIntradayChartEnabled;

  const {
    priceData,
    fromDateEST,
    toDateEST,
    lastProportionChange,
    lastKnownPrice,
    prevCloseData,
    lastKnownDate,
    isLoading: isPriceMarketReactionDataLoading,
  } = usePriceMarketReactionData({
    entityTag: tags[0],
    ticker: tickers[0],
    reportingDate: createdAt,
    isMultipleEntitiesChart: false,
    variant: 'catalyst',
    isMarketCatalyst: true,
  });

  const { data: entitiesData } = useEntities({
    entityTags: tags,
  });

  const { data: themeTimeseries, isLoading: isThemeTimeseriesLoading } =
    useThemePerformanceTimeseries(
      { themeId: themeId ?? '', horizon: 'intraday' },
      isThemeTimeseriesEnabled
    );

  const { data: themeData } = useTheme({
    themeId: isThemeTimeseriesEnabled ? themeId : undefined,
  });

  const themeAggregate = themeTimeseries?.aggregate;
  const themeName = themeData?.name;
  const hasIntradayThemeTimeseries = !!themeAggregate?.length && !!themeName;

  const etfLine = buildSingleEtfLine({
    priceData,
    prevCloseData,
    lastProportionChange,
    lastKnownPrice,
    lastKnownDate,
    asPercentChange: hasIntradayThemeTimeseries,
  });

  const lines: LineData[] = hasIntradayThemeTimeseries
    ? [
        {
          data: themeAggregate.map(({ index, value }) => ({
            time: index,
            value,
          })),
          label: themeName,
          lastProportionChange: themeAggregate.at(-1)?.value,
        },
        etfLine,
      ]
    : [etfLine];

  const entities = hasIntradayThemeTimeseries
    ? ([
        {
          name_short: themeData?.name ?? '',
          ticker: '',
        },
        ...entitiesData,
      ] as Entity[])
    : entitiesData;

  return {
    lines,
    fromDateEST,
    toDateEST,
    isLoading:
      isPriceMarketReactionDataLoading ||
      (isThemeTimeseriesEnabled && isThemeTimeseriesLoading),
    entities,
  };
};
